<!-- src/App.vue -->
<template>
  <div id="page-container" class="darkmode-bound blend-isolation-wrapper">
    <!-- Add PageLoader component -->
    <PageLoader />
    
    <!-- Router transition -->
    <RouterView v-slot="{ Component, route }">
      <Transition
        :name="typeof route.meta.transition === 'string' ? route.meta.transition : 'fade'"
        mode="out-in"
      >
        <div class="route-host" :key="route.fullPath">
          <component :is="Component" />
        </div>
      </Transition>
    </RouterView>

    <DarkModeToggle v-if="showToggle" />
    <div id="ripple-cancel-layer" aria-hidden="true"></div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, nextTick, computed, defineAsyncComponent, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useTheme } from "@/stores/useTheme";
import PageLoader from "@/components/PageLoader.vue"; 
import { usePerformanceStore } from "@/stores/usePerformanceCheck";

import type { ComponentPublicInstance } from "vue";

// Define the type for the overlay ref, assuming playTransition exists
type OverlayComponent = ComponentPublicInstance<{ playTransition: (toName: string, next: () => void) => void }>;

const overlay = ref<OverlayComponent | null>(null);

// Lazy-load the toggle to keep first paint snappy
const DarkModeToggle = defineAsyncComponent(
  () => import("@/components/DarkModeToggle.vue")
);

const theme = useTheme();
const route = useRoute();
const router = useRouter();
const perf = usePerformanceStore();

// Hide toggle on these routes
const HIDE_TOGGLE_ROUTES = ["Unauthorized", "NotFound"];
const showToggle = computed(() => !HIDE_TOGGLE_ROUTES.includes(route.name as string));

// Routes where we DO NOT run ripple/perf init (prevents WebGL/canvas on 0×0 during transition)
const BLOCK_PERF_ROUTES = new Set(["Login", "Register", "Unauthorized", "NotFound"]);

onMounted(async () => {
  // Ensure CSS vars are applied before anything else
  theme.initTheme();

  // Defer perf/ripple init and skip on auth pages (fixes zero-size WebGL on login)
  await nextTick();
  if (!BLOCK_PERF_ROUTES.has(route.name as string)) {
    // double RAF ensures the new route has real size before any canvas/WebGL starts
    requestAnimationFrame(() => requestAnimationFrame(() => perf.detect()));
  }

});
</script>

<style scoped>
/* Guarantee a non-zero container so anything measuring size never sees 0×0 */
#page-container { min-height: 100dvh; }

/* Keep the transition child as a single “element”; contents = no extra box */
.route-host { display: contents; }
</style>
